import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import { RootState } from "us.helper/types";
import * as Actions from "us.common/actions";
import {
  $Affix,
  $PageHeader,
  $Divider,
  $Button,
  $Drawer,
} from "us.common/components";
import { PlusOutlined } from "us.icons";
import { PostalAreaDetails, PostalAreasTable } from "./components";
import "./Home.scss";
import { useSafeBackNavigation } from "us.helper/navigation";

// Interface for postal area record
type PostalArea = {
  postalCodeId: number;
  postalCode: string;
  postalPlace: string;
  countyCode: string;
  countyName: string;
  countryCode: string;
  country: string;
  isUsed?: boolean;
};

/**
 * @description - Postal Areas home view component for managing postal area add/edit operations.
 * @author Roshan Maddumage <roshanma@unicorn-solutions.com>
 * @since 2025-05-29
 */
const PostalAreas: React.FC<PropsFromRedux> = (props) => {
  const { t } = useTranslation();
  const goBack = useSafeBackNavigation();

  const { drawer, searchPostalAreas, setDrawer, deletePostalArea } = props;

  useEffect(() => {
    searchPostalAreas && searchPostalAreas({ countryCode: "", searchText: "" });
  }, []);

  // Handle postal area actions from table
  const handlePostalAreaAction = (
    action: "EDIT" | "VIEW" | "DELETE",
    record: PostalArea,
  ) => {
    switch (action) {
      case "DELETE":
        deletePostalArea &&
          deletePostalArea({
            data: record,
            countryCode: "",
          });
        break;
      default:
        setDrawer &&
          setDrawer({
            visible: true,
            action,
            record,
          });
    }
  };

  const closeDrawer = () => {
    setDrawer &&
      setDrawer({
        visible: false,
        action: "NEW",
        record: null,
      });
  };

  return (
    <>
      <$Affix offsetTop={48}>
        <div className="page-header header-border">
          <div className="d-flex align-items-center">
            <$PageHeader
              className="px-0"
              onBack={goBack}
              title={t(
                "US.COLLECTION.ADMIN:MANAGE_POSTAL_AREAS.MANAGE_POSTAL_AREAS",
              )}
            />
            <$Divider className="bui-devider" type="vertical" />
            <$Button
              className="mr-2"
              type="default"
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                setDrawer &&
                setDrawer({
                  visible: true,
                  action: "NEW",
                  record: null,
                })
              }
            >
              {t("US.COLLECTION.ADMIN:MANAGE_POSTAL_AREAS.ADD_NEW_POSTAL_AREA")}
            </$Button>
          </div>
        </div>
      </$Affix>{" "}
      <div className="space-content manage-postal-areas">
        <PostalAreasTable
          onAction={(action, record) => handlePostalAreaAction(action, record)}
        />
      </div>
      <$Drawer
        title={
          drawer.action === "NEW"
            ? t("US.COLLECTION.ADMIN:MANAGE_POSTAL_AREAS.ADD_NEW_POSTAL_AREA")
            : drawer.action === "EDIT"
            ? t("US.COLLECTION.ADMIN:MANAGE_POSTAL_AREAS.EDIT_POSTAL_AREA")
            : t("US.COLLECTION.ADMIN:MANAGE_POSTAL_AREAS.VIEW_POSTAL_AREA")
        }
        width={"850px"}
        open={drawer.visible}
        onClose={closeDrawer}
        destroyOnClose={true}
        maskClosable={false}
      >
        <PostalAreaDetails
          onCancel={closeDrawer}
          drawerAction={drawer.action}
          selectedRecord={drawer.record}
        />
      </$Drawer>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { entityDetails, postalAreas } = state;

  return {
    entityDetails,
    postalAreas: postalAreas.postalAreas,
    drawer: postalAreas.postalAreaDrawer,
  };
};

const { postalArea, deletePostalArea } = Actions.postalAreas;
const { getPostalAreas } = Actions.entity;

const mapDispatchToProps = {
  searchPostalAreas: getPostalAreas,
  savePostalArea: postalArea.save,
  setDrawer: postalArea.openDrawer,
  deletePostalArea: deletePostalArea.delete,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(PostalAreas);
